import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { useWallet } from '../contexts/WalletContext';

interface WalletSessionModalProps {
  onClose: () => void;
}

export const WalletSessionModal: React.FC<WalletSessionModalProps> = ({ onClose }) => {
  const { sessionInfo, cancelSession } = useWallet();
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');

  const pairingUri = sessionInfo?.pairingUri;

  useEffect(() => {
    let isMounted = true;
    const generateQr = async () => {
      if (!pairingUri) {
        setQrImage(null);
        return;
      }
      try {
        const dataUrl = await QRCode.toDataURL(pairingUri, {
          errorCorrectionLevel: 'H',
          scale: 8,
          margin: 1,
        });
        if (isMounted) {
          setQrImage(dataUrl);
        }
      } catch (error) {
        console.error('[WalletSessionModal] ❌ Failed to render QR code', error);
        if (isMounted) {
          setQrImage(null);
        }
      }
    };

    generateQr();

    return () => {
      isMounted = false;
    };
  }, [pairingUri]);

  const statusLabel = useMemo(() => {
    if (!sessionInfo) return 'Unknown';
    switch (sessionInfo.status) {
      case 'pending':
        return 'Pending Authorization';
      case 'approved':
        return 'Authorized';
      case 'rejected':
        return 'Rejected';
      case 'expired':
        return 'Expired';
      case 'terminated':
        return 'Terminated';
      default:
        return sessionInfo.status;
    }
  }, [sessionInfo]);

  if (!sessionInfo) {
    return null;
  }

  const handleCopy = async () => {
    if (!pairingUri) {
      return;
    }
    try {
      await navigator.clipboard.writeText(pairingUri);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch (error) {
      console.error('[WalletSessionModal] ❌ Failed to copy pairing URI', error);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  const handleCancel = async () => {
    await cancelSession();
    onClose();
  };

  const createdAt = sessionInfo.createdAt ? new Date(sessionInfo.createdAt) : null;
  const expiresAt = sessionInfo.expiresAt ? new Date(sessionInfo.expiresAt) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Wallet Connection Authorization
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Please confirm this request in GiGi Wallet App's "DApp Authorization Center"
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 p-4 dark:border-gray-700">
            {qrImage ? (
              <img
                src={qrImage}
                alt="WalletConnect QR"
                className="h-48 w-48 rounded-lg border border-gray-200 shadow-sm dark:border-gray-700"
              />
            ) : (
              <div className="flex h-48 w-full flex-col items-center justify-center rounded-lg bg-gray-50 text-center text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                <span className="font-medium">No Pairing URI</span>
                <span>Please scan Session ID in GiGi Wallet App</span>
              </div>
            )}
            {pairingUri && (
              <button
                onClick={handleCopy}
                className="mt-3 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {copyStatus === 'copied' ? 'Copied ✅' : copyStatus === 'error' ? 'Copy Failed' : 'Copy Pairing URI'}
              </button>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/60">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Session Info</h3>
              <dl className="mt-2 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <div>
                  <dt className="font-medium text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="text-base font-semibold text-blue-600 dark:text-blue-400">{statusLabel}</dd>
                </div>
                <div className="break-all">
                  <dt className="font-medium text-gray-500 dark:text-gray-400">Session ID</dt>
                  <dd className="font-mono text-xs">{sessionInfo.sessionId}</dd>
                </div>
                {sessionInfo.dappName && (
                  <div>
                    <dt className="font-medium text-gray-500 dark:text-gray-400">DApp</dt>
                    <dd>{sessionInfo.dappName}</dd>
                  </div>
                )}
                {createdAt && (
                  <div>
                    <dt className="font-medium text-gray-500 dark:text-gray-400">Created At</dt>
                    <dd>{createdAt.toLocaleString()}</dd>
                  </div>
                )}
                {expiresAt && (
                  <div>
                    <dt className="font-medium text-gray-500 dark:text-gray-400">Expires At</dt>
                    <dd>{expiresAt.toLocaleString()}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="rounded-xl bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
              <ol className="list-decimal space-y-2 pl-4">
                <li>Open GiGi Wallet App on your phone → Settings → DApp Authorization Center</li>
                <li>Select "Scan Session" or paste Pairing URI</li>
                <li>After confirming authorization, this page will connect automatically</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Cancel Authorization
          </button>
          <button
            onClick={onClose}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalletSessionModal;

